import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const MatchResultCard = ({ match, onSaveDraft, onPublishResult, teams, players, defaultExpanded = false }) => {
  const [resultData, setResultData] = useState({
    team1Score: match?.team1Score || '',
    team1Wickets: match?.team1Wickets || '',
    team2Score: match?.team2Score || '',
    team2Wickets: match?.team2Wickets || '',
    winnerId: match?.winnerId || '',
    tossWinnerId: match?.tossWinnerId || '',
    battingFirstId: match?.battingFirstId || '',
    manOfMatchId: match?.manOfMatchId || '',
    highestScorerId: match?.highestScorerId || '',
    highestWicketTakerId: match?.highestWicketTakerId || '',
    matchStatus: match?.status === 'cancelled' ? 'cancelled' : (match?.matchStatus || '')
  });

  const [showDetails, setShowDetails] = useState(false);
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [isEditing, setIsEditing] = useState(match?.resultsStatus !== 'published');

  const handleInputChange = (field, value) => {
    setResultData(prev => ({ ...prev, [field]: value }));
  };

  // Filter teams to only show the 2 teams playing in this match
  const matchTeams = teams?.filter(team => 
    team?.id === match?.team1Id || team?.id === match?.team2Id
  );

  const teamOptions = [
    ...(matchTeams?.map(team => ({
      value: team?.id,
      label: team?.shortName ? `${team?.shortName} - ${team?.name}` : team?.name
    })) || []),
    { value: 'no_result', label: 'No Result' }
  ];

  // Filter players to only show players from the 2 teams playing in this match
  const matchPlayers = players?.filter(player => 
    player?.teamId === match?.team1Id || player?.teamId === match?.team2Id
  );

  const playerOptions = matchPlayers?.map(player => ({
    value: player?.id,
    label: `${player?.name} (${player?.team})`
  }));

  const getStatusBadge = () => {
    if (resultData?.matchStatus === 'cancelled' || match?.status === 'cancelled') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-warning/10 text-warning text-xs font-medium rounded">
          <Icon name="XCircle" size={14} />
          Cancelled
        </span>
      );
    }
    if (match?.resultsStatus === 'draft') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-warning/10 text-warning text-xs font-medium rounded">
          <Icon name="Edit" size={14} />
          Draft
        </span>
      );
    }
    
    if (match?.resultsStatus === 'published' || match?.status === 'completed') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-success/10 text-success text-xs font-medium rounded">
          <Icon name="CheckCircle" size={14} />
          Published
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 bg-error/10 text-error text-xs font-medium rounded">
        <Icon name="Clock" size={14} />
        Pending
      </span>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden">
      {/* Collapsed Header - always visible */}
      <div
        className="p-4 md:p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 cursor-pointer hover:bg-muted/30 transition-colors"
        onClick={() => setIsExpanded(prev => !prev)}
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-shrink-0">
            <Image
              src={match?.team1Logo}
              alt={match?.team1LogoAlt}
              className="w-7 h-7 rounded object-cover"
            />
            <span className="font-medium text-foreground text-sm">
              {match?.team1ShortName || match?.team1}
            </span>
          </div>
          <span className="text-xs text-muted-foreground font-medium">vs</span>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Image
              src={match?.team2Logo}
              alt={match?.team2LogoAlt}
              className="w-7 h-7 rounded object-cover"
            />
            <span className="font-medium text-foreground text-sm">
              {match?.team2ShortName || match?.team2}
            </span>
          </div>
          <span className="hidden sm:inline text-xs text-muted-foreground ml-2">
            Match #{match?.matchNumber}
          </span>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          {getStatusBadge()}
          <span className="text-xs text-muted-foreground hidden md:inline">
            {formatDate(match?.date)}
          </span>
          <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={16} className="text-muted-foreground" />
        </div>
      </div>
      {/* Expanded Content */}
      {isExpanded && (
        <>
          <div className="p-4 md:p-6 border-t border-border">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-lg font-semibold text-foreground">
                    Match #{match?.matchNumber}
                  </h3>
                  {getStatusBadge()}
                </div>
                <p className="text-sm text-muted-foreground">
                  {formatDate(match?.date)} • {match?.venue}
                </p>
              </div>
              
              {resultData?.matchStatus !== 'cancelled' && (
                <Button
                  variant="ghost"
                  onClick={(e) => { e?.stopPropagation(); setShowDetails(!showDetails); }}
                  iconName={showDetails ? 'ChevronUp' : 'ChevronDown'}
                  iconPosition="right"
                >
                  {showDetails ? 'Hide Details' : 'Show Details'}
                </Button>
              )}
            </div>

            {/* Match Status (Cancelled) selector */}
            {isEditing && (
              <div className="mb-6">
                <Select
                  label="Match Status"
                  options={[
                    { value: '', label: 'Normal (Result Available)' },
                    { value: 'cancelled', label: 'Cancelled' }
                  ]}
                  value={resultData?.matchStatus}
                  onChange={(value) => handleInputChange('matchStatus', value)}
                  placeholder="Select match status"
                  disabled={!isEditing}
                />
              </div>
            )}

            {resultData?.matchStatus !== 'cancelled' && (
              <>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                  <div className="space-y-4">
                    <div className="flex items-center gap-4">
                      <Image
                        src={match?.team1Logo}
                        alt={match?.team1LogoAlt}
                        className="w-12 h-12 rounded-lg object-cover"
                      />
                      <div className="flex-1">
                        <p className="font-medium text-foreground">
                          {match?.team1ShortName ? `${match?.team1ShortName} - ${match?.team1}` : match?.team1}
                        </p>
                        <div className="flex items-center gap-2 mt-2">
                          <Input
                            type="number"
                            placeholder="Runs"
                            value={resultData?.team1Score}
                            onChange={(e) => handleInputChange('team1Score', e?.target?.value)}
                            className="w-24"
                            disabled={!isEditing}
                          />
                          <span className="text-muted-foreground">/</span>
                          <Input
                            type="number"
                            placeholder="Wickets"
                            value={resultData?.team1Wickets}
                            onChange={(e) => handleInputChange('team1Wickets', e?.target?.value)}
                            className="w-24"
                            disabled={!isEditing}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center gap-4">
                      <Image
                        src={match?.team2Logo}
                        alt={match?.team2LogoAlt}
                        className="w-12 h-12 rounded-lg object-cover"
                      />
                      <div className="flex-1">
                        <p className="font-medium text-foreground">
                          {match?.team2ShortName ? `${match?.team2ShortName} - ${match?.team2}` : match?.team2}
                        </p>
                        <div className="flex items-center gap-2 mt-2">
                          <Input
                            type="number"
                            placeholder="Runs"
                            value={resultData?.team2Score}
                            onChange={(e) => handleInputChange('team2Score', e?.target?.value)}
                            className="w-24"
                            disabled={!isEditing}
                          />
                          <span className="text-muted-foreground">/</span>
                          <Input
                            type="number"
                            placeholder="Wickets"
                            value={resultData?.team2Wickets}
                            onChange={(e) => handleInputChange('team2Wickets', e?.target?.value)}
                            className="w-24"
                            disabled={!isEditing}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mb-6">
                  <Select
                    label="Match Winner"
                    options={teamOptions}
                    value={resultData?.winnerId}
                    onChange={(value) => handleInputChange('winnerId', value)}
                    placeholder="Select winning team"
                    required
                    disabled={!isEditing}
                  />
                </div>

                {showDetails && (
                  <div className="space-y-4 pt-4 border-t border-border">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Select
                        label="Toss Winner"
                        options={teamOptions}
                        value={resultData?.tossWinnerId}
                        onChange={(value) => handleInputChange('tossWinnerId', value)}
                        placeholder="Select toss winner"
                        disabled={!isEditing}
                      />
                      
                      <Select
                        label="Batting First"
                        options={teamOptions}
                        value={resultData?.battingFirstId}
                        onChange={(value) => handleInputChange('battingFirstId', value)}
                        placeholder="Select batting first team"
                        disabled={!isEditing}
                      />
                    </div>

                    <Select
                      label="Man of the Match"
                      options={playerOptions}
                      value={resultData?.manOfMatchId}
                      onChange={(value) => handleInputChange('manOfMatchId', value)}
                      placeholder="Select MOTM"
                      searchable
                      disabled={!isEditing}
                    />

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <Select
                        label="Highest Run Scorer"
                        options={playerOptions}
                        value={resultData?.highestScorerId}
                        onChange={(value) => handleInputChange('highestScorerId', value)}
                        placeholder="Select player"
                        searchable
                        disabled={!isEditing}
                      />
                      
                      <Select
                        label="Highest Wicket Taker"
                        options={playerOptions}
                        value={resultData?.highestWicketTakerId}
                        onChange={(value) => handleInputChange('highestWicketTakerId', value)}
                        placeholder="Select player"
                        searchable
                        disabled={!isEditing}
                      />
                    </div>
                  </div>
                )}
              </>
            )}

            {resultData?.matchStatus === 'cancelled' && (
              <div className="mb-6 p-4 bg-warning/10 border border-warning/20 rounded-lg">
                <div className="flex items-center gap-2 text-warning">
                  <Icon name="AlertTriangle" size={16} />
                  <p className="text-sm font-medium">This match will be published as Cancelled. No result details are required.</p>
                </div>
              </div>
            )}
          </div>
          <div className="px-4 md:px-6 py-4 bg-muted border-t border-border flex flex-col sm:flex-row gap-3">
            {match?.resultsStatus === 'published' ? (
              isEditing ? (
                <>
                  <Button
                    variant="default"
                    onClick={() => {
                      onPublishResult(match?.id, resultData, true);
                      setIsEditing(false);
                    }}
                    iconName="Save"
                    iconPosition="left"
                    className="flex-1 sm:flex-initial"
                    disabled={
                      resultData?.matchStatus !== 'cancelled' && (
                        !resultData?.winnerId || (resultData?.winnerId !== 'no_result' && (!resultData?.team1Score || !resultData?.team2Score))
                      )
                    }
                  >
                    Save Changes
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setIsEditing(false)}
                    iconName="X"
                    iconPosition="left"
                    className="flex-1 sm:flex-initial"
                  >
                    Cancel Edit
                  </Button>
                </>
              ) : (
                <Button
                  variant="default"
                  onClick={() => setIsEditing(!isEditing)}
                  iconName="Edit"
                  iconPosition="left"
                  className="flex-1 sm:flex-initial"
                >
                  Edit Result
                </Button>
              )
            ) : (
              <>
                <Button
                  variant="outline"
                  onClick={() => onSaveDraft(match?.id, resultData)}
                  iconName="Save"
                  iconPosition="left"
                  className="flex-1 sm:flex-initial"
                >
                  Save Draft
                </Button>
                <Button
                  variant="default"
                  onClick={() => onPublishResult(match?.id, resultData)}
                  iconName="CheckCircle"
                  iconPosition="left"
                  className="flex-1 sm:flex-initial"
                  disabled={
                    resultData?.matchStatus !== 'cancelled' && (
                      !resultData?.winnerId || (resultData?.winnerId !== 'no_result' && (!resultData?.team1Score || !resultData?.team2Score))
                    )
                  }
                >
                  Publish Result
                </Button>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default MatchResultCard;